<template>
  <div class="gestao-tab">
    <div class="gestao-toolbar">
      <div class="filter-wrap" ref="filterWrapEl">
        <button class="btn-toolbar" type="button" :class="{ active: filterOpen }" @click="filterOpen = !filterOpen">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Filtrar cargos
          <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
        </button>

        <div v-if="filterOpen" class="filter-dropdown">
          <div class="filter-title">Cargos visíveis</div>
          <label class="filter-opt">
            <input type="checkbox" :checked="filterRoleIds.length === 0" @change="clearFilter" />
            <span>Todos</span>
          </label>
          <label v-for="role in roles" :key="role.id" class="filter-opt">
            <input type="checkbox" :checked="filterRoleIds.includes(role.id)" @change="toggleFilter(role.id)" />
            <span>{{ role.name }}</span>
            <span class="filter-count">{{ countByRole(role.id) }}</span>
          </label>
          <label class="filter-opt">
            <input type="checkbox" :checked="filterRoleIds.includes('__none__')" @change="toggleFilter('__none__')" />
            <span>Sem cargo</span>
            <span class="filter-count">{{ countByRole('__none__') }}</span>
          </label>
        </div>
      </div>

      <div class="view-toggle">
        <button type="button" class="toggle-btn" :class="{ active: viewMode === 'projects' }" @click="setViewMode('projects')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
          Por projetos
        </button>
        <button type="button" class="toggle-btn" :class="{ active: viewMode === 'roles' }" @click="setViewMode('roles')">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
          </svg>
          Por cargo
        </button>
      </div>

      <button class="btn-toolbar btn-toolbar--ghost" type="button" :class="{ active: showUnassignedUsers }" @click="showUnassignedUsers = !showUnassignedUsers">
        {{ showUnassignedUsers ? 'Ocultar sem projeto' : 'Mostrar usuários sem projeto atribuído' }}
      </button>

      <div class="date-pick-wrap">
        <button class="btn-nav" type="button" @click="shiftDate(-1)">‹</button>
        <input type="date" class="date-input" :value="selectedDate" @change="onDateChange" />
        <button class="btn-nav" type="button" @click="shiftDate(1)">›</button>
        <span class="date-label">{{ formatDateLabel(selectedDate) }}</span>
      </div>
    </div>

    <div v-if="loading" class="gestao-empty">Carregando...</div>
    <div v-else-if="displayGroups.length === 0" class="gestao-empty">Nenhum usuário encontrado.</div>

    <div v-else class="folders-list">
      <div v-for="group in displayGroups" :key="group.groupId" class="folder">
        <button class="folder-header" type="button" @click="toggleFolder(group.groupId)">
          <span class="folder-icon">
            <svg v-if="openFolders.includes(group.groupId)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </span>
          <svg class="folder-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <span class="folder-name">{{ group.groupName }}</span>
          <div class="folder-stats">
            <span class="folder-stat folder-stat--present">{{ group.counters.present }} presentes</span>
            <span class="folder-stat folder-stat--absent">{{ group.counters.absent }} ausentes</span>
          </div>
          <span class="folder-count">{{ group.users.length }}</span>
        </button>

        <div v-if="openFolders.includes(group.groupId)" class="folder-content">
          <div
            v-for="user in group.users"
            :key="user.username"
            class="user-card"
            :class="{ 'user-card--streak': highlightedStreak(user) >= 3 }"
          >
            <div class="user-card-head">
              <div class="uc-avatar">
                <img v-if="user.avatar" :src="$resolveUrl(user.avatar)" alt="perfil" />
                <span v-else class="uc-initials">{{ initials(user.name) }}</span>
              </div>
              <div class="uc-identity">
                <strong class="uc-name">{{ user.name }}</strong>
                <span class="uc-username">@{{ user.username }}</span>
                <span v-if="viewMode === 'projects' && user.roleName" class="uc-role">{{ user.roleName }}</span>
              </div>
            </div>

            <div v-if="viewMode === 'projects'" class="uc-assignments">
              <span class="presence-pill" :class="presenceClass(user.status)">
                {{ presenceLabel(user.status, user.time) }}
              </span>
              <span v-if="user.absentStreak >= 3" class="streak-badge">
                Ausente há {{ user.absentStreak }} dias
              </span>
            </div>

            <div v-else class="uc-assignments">
              <div v-if="user.projectAttendances.length > 0" class="assign-list">
                <div class="assign-section-label">Projetos em {{ formatDateLabel(selectedDate) }}:</div>
                <div
                  v-for="project in user.projectAttendances"
                  :key="project.projectId"
                  class="assign-chip"
                  :class="assignChipClass(project.status)"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
                  </svg>
                  {{ project.projectName }}
                  <span class="chip-sub">{{ presenceLabel(project.status, project.time) }}</span>
                  <span v-if="project.absentStreak >= 3" class="chip-streak">{{ project.absentStreak }} dias</span>
                </div>
              </div>
              <div v-else class="assign-empty">Sem atribuições em projetos</div>
              <span v-if="!user.hasAssignedProject" class="unassigned-badge">Sem projeto atribuído</span>
              <span v-else-if="user.maxAbsentStreak >= 3" class="streak-badge">Ausente há {{ user.maxAbsentStreak }} dias</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch, type PropType } from 'vue'
import { apiFetch } from '@/store/api'
import type { Account } from '@/store/accounts'
import type { Role } from '@/store/roles'

interface ProjectModeUser {
  username: string
  name: string
  avatar: string
  roleId: string
  roleName: string
  status: 'present' | 'absent' | 'optional'
  time: string
  absentStreak: number
}

interface RoleModeProjectAttendance {
  projectId: string
  projectName: string
  status: 'present' | 'absent' | 'optional'
  time: string
  absentStreak: number
}

interface RoleModeUser {
  username: string
  name: string
  avatar: string
  roleId: string
  roleName: string
  projectAttendances: RoleModeProjectAttendance[]
  hasAssignedProject: boolean
  maxAbsentStreak: number
}

interface GroupCounters {
  present: number
  absent: number
}

interface OverviewGroup<TUser> {
  groupId: string
  groupName: string
  users: TUser[]
  counters: GroupCounters
}

interface AttendanceOverviewPayload {
  date: string
  workedWeekdays: string[]
  projects: OverviewGroup<ProjectModeUser>[]
  roles: OverviewGroup<RoleModeUser>[]
}

type DisplayProjectGroup = OverviewGroup<ProjectModeUser>
type DisplayRoleGroup = OverviewGroup<RoleModeUser>

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function shiftIsoDate(iso: string, days: number) {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

export default defineComponent({
  name: 'UsersGestaoTab',
  props: {
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    roles: { type: Array as PropType<Role[]>, default: () => [] }
  },
  setup(props) {
    const viewMode = ref<'projects' | 'roles'>('projects')
    const selectedDate = ref(todayIso())
    const filterRoleIds = ref<string[]>([])
    const openFolders = ref<string[]>([])
    const filterOpen = ref(false)
    const filterWrapEl = ref<HTMLElement | null>(null)
    const loading = ref(false)
    const showUnassignedUsers = ref(false)
    const overviewWorkedWeekdays = ref<string[]>(['mon', 'tue', 'wed', 'thu', 'fri'])
    const projectGroups = ref<OverviewGroup<ProjectModeUser>[]>([])
    const roleGroups = ref<OverviewGroup<RoleModeUser>[]>([])

    const initials = (name: string) =>
      (name || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()

    const formatDateLabel = (iso: string) => {
      if (!iso) return ''
      try {
        const [y, m, d] = iso.split('-')
        return `${d}/${m}/${y}`
      } catch {
        return iso
      }
    }

    const weekdayKeyFromIso = (iso: string) => {
      const date = new Date(`${iso}T00:00:00`)
      if (Number.isNaN(date.getTime())) return 'mon'
      return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][date.getDay()] || 'mon'
    }

    const isWorkedDayForSelectedDate = computed(() => overviewWorkedWeekdays.value.includes(weekdayKeyFromIso(selectedDate.value)))

    const highlightedStreak = (user: ProjectModeUser | RoleModeUser) =>
      'absentStreak' in user ? Number(user.absentStreak || 0) : Number(user.maxAbsentStreak || 0)

    const presenceLabel = (status: 'present' | 'absent' | 'optional', time = '') => {
      if (status === 'present') return `Presente${time ? ` • ${time}` : ''}`
      if (status === 'optional') return 'Opcional'
      return 'Ausente'
    }

    const presenceClass = (status: 'present' | 'absent' | 'optional') =>
      status === 'present'
        ? 'presence-pill--present'
        : status === 'optional'
          ? 'presence-pill--optional'
          : 'presence-pill--absent'

    const assignChipClass = (status: 'present' | 'absent' | 'optional') =>
      status === 'present'
        ? 'assign-chip-present'
        : status === 'optional'
          ? 'assign-chip-optional'
          : 'assign-chip-absent'

    const makeCounters = (users: ProjectModeUser[]): GroupCounters =>
      users.reduce((acc, user) => {
        if (user.status === 'present') acc.present += 1
        else if (user.status === 'absent') acc.absent += 1
        return acc
      }, { present: 0, absent: 0 })

    const unassignedProjectGroup = computed<DisplayProjectGroup | null>(() => {
      if (!showUnassignedUsers.value) return null
      const users = roleGroups.value
        .flatMap((group) => group.users)
        .filter((user) => !user.hasAssignedProject)
        .map((user) => ({
          username: user.username,
          name: user.name,
          avatar: user.avatar,
          roleId: user.roleId,
          roleName: user.roleName,
          status: (isWorkedDayForSelectedDate.value ? 'absent' : 'optional') as 'absent' | 'optional',
          time: '',
          absentStreak: isWorkedDayForSelectedDate.value ? Math.max(1, Number(user.maxAbsentStreak || 0)) : 0
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      if (users.length === 0) return null

      return {
        groupId: '__unassigned__',
        groupName: 'Sem projeto atribuído',
        users,
        counters: makeCounters(users)
      }
    })

    const displayGroups = computed<(DisplayProjectGroup | DisplayRoleGroup)[]>(() => {
      if (viewMode.value === 'projects') {
        const groups: DisplayProjectGroup[] = projectGroups.value
          .map((group) => {
            const users = group.users.filter((user) => {
              const roleKey = user.roleId && props.roles.some((role) => role.id === user.roleId) ? user.roleId : '__none__'
              return filterRoleIds.value.length === 0 || filterRoleIds.value.includes(roleKey)
            })
            return {
              ...group,
              users,
              counters: makeCounters(users)
            }
          })
          .filter((group) => group.users.length > 0)

        if (unassignedProjectGroup.value) {
          const roleFilteredUsers = unassignedProjectGroup.value.users.filter((user) => {
            const roleKey = user.roleId && props.roles.some((role) => role.id === user.roleId) ? user.roleId : '__none__'
            return filterRoleIds.value.length === 0 || filterRoleIds.value.includes(roleKey)
          })
          if (roleFilteredUsers.length > 0) {
            groups.push({
              ...unassignedProjectGroup.value,
              users: roleFilteredUsers,
              counters: makeCounters(roleFilteredUsers)
            })
          }
        }

        return groups
      }

      return roleGroups.value
        .map((group) => {
          const users = group.users.filter((user) => {
            const allowRole = filterRoleIds.value.length === 0 || filterRoleIds.value.includes(group.groupId)
            const allowProjectState = showUnassignedUsers.value || user.hasAssignedProject
            return allowRole && allowProjectState
          })
          return {
            ...group,
            users,
            counters: users.reduce((acc, user) => {
              acc.present += user.projectAttendances.filter((item) => item.status === 'present').length
              acc.absent += user.projectAttendances.filter((item) => item.status === 'absent').length
              return acc
            }, { present: 0, absent: 0 })
          }
        })
        .filter((group) => group.users.length > 0)
    })

    const activeFilterCount = computed(() => filterRoleIds.value.length)

    const countByRole = (roleId: string) => {
      return props.accounts.filter((a) =>
        roleId === '__none__'
          ? !a.roleId || !props.roles.find((r) => r.id === a.roleId)
          : a.roleId === roleId
      ).length
    }

    const clearFilter = () => { filterRoleIds.value = [] }

    const toggleFilter = (roleId: string) => {
      const idx = filterRoleIds.value.indexOf(roleId)
      if (idx >= 0) filterRoleIds.value.splice(idx, 1)
      else filterRoleIds.value.push(roleId)
    }

    const toggleFolder = (groupId: string) => {
      const idx = openFolders.value.indexOf(groupId)
      if (idx >= 0) openFolders.value.splice(idx, 1)
      else openFolders.value.push(groupId)
    }

    const setViewMode = (mode: 'projects' | 'roles') => {
      viewMode.value = mode
    }

    const onDateChange = (e: Event) => {
      selectedDate.value = (e.target as HTMLInputElement).value
    }

    const shiftDate = (days: number) => {
      selectedDate.value = shiftIsoDate(selectedDate.value, days)
    }

    const loadAttendanceOverview = async () => {
      loading.value = true
      try {
        const payload = await apiFetch<AttendanceOverviewPayload>(`/user-attendance-overview?date=${selectedDate.value}`)
        overviewWorkedWeekdays.value = Array.isArray(payload.workedWeekdays) && payload.workedWeekdays.length > 0
          ? payload.workedWeekdays
          : ['mon', 'tue', 'wed', 'thu', 'fri']
        projectGroups.value = payload.projects || []
        roleGroups.value = payload.roles || []
      } catch {
        // ignore
      } finally {
        loading.value = false
      }
    }

    watch(displayGroups, (groups) => {
      const existing = new Set(openFolders.value)
      for (const group of groups) {
        if (!existing.has(group.groupId)) openFolders.value.push(group.groupId)
      }
    }, { immediate: true })

    watch(selectedDate, () => {
      loadAttendanceOverview()
    })

    const onDocClick = (e: MouseEvent) => {
      if (filterWrapEl.value && !filterWrapEl.value.contains(e.target as Node)) {
        filterOpen.value = false
      }
    }

    onMounted(() => {
      loadAttendanceOverview()
      document.addEventListener('click', onDocClick)
    })

    onUnmounted(() => {
      document.removeEventListener('click', onDocClick)
    })

    return {
      viewMode,
      selectedDate,
      filterRoleIds,
      openFolders,
      filterOpen,
      filterWrapEl,
      loading,
      showUnassignedUsers,
      displayGroups,
      activeFilterCount,
      initials,
      formatDateLabel,
      presenceLabel,
      presenceClass,
      assignChipClass,
      countByRole,
      clearFilter,
      toggleFilter,
      toggleFolder,
      setViewMode,
      onDateChange,
      shiftDate,
      highlightedStreak
    }
  }
})
</script>

<style scoped lang="scss">
.gestao-tab {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.gestao-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(31, 30, 26, .1);
  margin-bottom: 14px;
}

.btn-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  background: var(--surface, #f5f3ee);
  border: 1.5px solid transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #2c2b27);
  cursor: pointer;
  transition: background .15s, border-color .15s;

  &:hover { background: #ece9e2; }
  &.active { border-color: var(--accent, #2f6fce); color: var(--accent, #2f6fce); }
}

.btn-toolbar--ghost {
  background: rgba(37, 99, 235, .08);
}

.filter-badge {
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: var(--accent, #2f6fce);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.filter-wrap { position: relative; }

.filter-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
  padding: 10px;
  min-width: 220px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted, #666);
  margin-bottom: 4px;
}

.filter-opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  color: var(--ink, #2c2b27);
}

.filter-count {
  color: var(--ink-muted, #888);
  font-size: 12px;
}

.view-toggle {
  display: inline-flex;
  align-items: center;
  background: #f4f4f5;
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;

  &.active {
    background: #fff;
    color: var(--accent, #2f6fce);
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
  }
}

.date-pick-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
}

.btn-nav {
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f5f3ee;
  cursor: pointer;
}

.date-input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 7px 10px;
}

.date-label {
  font-size: 12px;
  color: var(--ink-muted, #666);
  font-weight: 600;
}

.gestao-empty {
  padding: 30px 10px;
  color: var(--ink-muted, #888);
  text-align: center;
  font-size: 14px;
}

.folders-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.folder {
  border: 1px solid rgba(31, 30, 26, .08);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.folder-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: none;
  background: #faf8f3;
  cursor: pointer;
  text-align: left;
}

.folder-icon,
.folder-svg {
  color: var(--accent, #2f6fce);
}

.folder-name {
  font-weight: 700;
  color: var(--ink, #2c2b27);
}

.folder-stats {
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
}

.folder-stat {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.folder-stat--present {
  background: rgba(34, 197, 94, .12);
  color: #15803d;
}

.folder-stat--absent {
  background: rgba(239, 68, 68, .1);
  color: #dc2626;
}

.folder-count {
  color: var(--ink-muted, #666);
  font-size: 12px;
  font-weight: 700;
}

.folder-content {
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.user-card {
  border: 1px solid rgba(31, 30, 26, .08);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-card--streak {
  box-shadow: 0 0 0 2px rgba(239, 68, 68, .12);
  background: linear-gradient(180deg, rgba(254, 242, 242, .8) 0%, #fff 100%);
}

.user-card-head {
  display: flex;
  gap: 10px;
  align-items: center;
}

.uc-avatar {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  overflow: hidden;
  background: #ece9e2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.uc-initials {
  font-size: 12px;
  font-weight: 800;
  color: var(--accent, #2f6fce);
}

.uc-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.uc-name {
  color: var(--ink, #2c2b27);
}

.uc-username {
  font-size: 12px;
  color: var(--ink-muted, #666);
}

.uc-role {
  width: fit-content;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(37, 99, 235, .12);
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
}

.presence-pill,
.streak-badge,
.unassigned-badge {
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.presence-pill--present {
  background: rgba(34, 197, 94, .12);
  color: #15803d;
}

.presence-pill--absent,
.streak-badge {
  background: rgba(239, 68, 68, .1);
  color: #dc2626;
}

.presence-pill--optional {
  background: rgba(245, 158, 11, .14);
  color: #b45309;
}

.unassigned-badge {
  background: rgba(15, 23, 42, .08);
  color: #475569;
}

.assign-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.assign-section-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted, #666);
}

.assign-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  flex-wrap: wrap;
}

.assign-chip-present {
  background: rgba(34, 197, 94, .12);
  color: #15803d;
}

.assign-chip-absent {
  background: rgba(239, 68, 68, .1);
  color: #dc2626;
}

.assign-chip-optional {
  background: rgba(245, 158, 11, .14);
  color: #b45309;
}

.chip-sub,
.chip-streak {
  font-size: 11px;
  opacity: .9;
}

.chip-streak {
  font-weight: 700;
}

.assign-empty {
  color: var(--ink-muted, #888);
  font-size: 13px;
}

@media (max-width: 900px) {
  .date-pick-wrap {
    margin-left: 0;
  }
}

@media (max-width: 720px) {
  .folder-header {
    flex-wrap: wrap;
  }

  .folder-stats {
    margin-left: 0;
  }

  .folder-content {
    grid-template-columns: 1fr;
  }
}
</style>
